import React, { useEffect, useRef, useState, useMemo } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { usePublicManufacturers } from '@src/hooks/queries/useManufacturers'
import { colors } from '@src/theme/sharedComponents'

const SCROLL_SPEED_PX_S = 60
const LOGO_SLOT_PX = 380

const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(var(--marquee-offset)); }
`

const SliderContainer = styled.div<{ $loading: boolean; $compact: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: ${colors.primaryDark};
  border-top: 2px solid ${colors.primary};
  border-bottom: 2px solid ${colors.primary};
  padding: ${({ $compact }) => $compact ? '8px 0' : '16px 0'};
  min-height: ${({ $compact }) => $compact ? '72px' : '110px'};
  display: flex;
  align-items: center;

  ${({ $loading }) => $loading && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: ${shimmer} 1.8s ease-in-out infinite;
      pointer-events: none;
    }
  `}
`

const SliderTrack = styled.div<{ $duration: number; $paused: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  animation: ${marqueeScroll} ${p => p.$duration}s linear infinite;
  animation-play-state: ${p => p.$paused ? 'paused' : 'running'};
  will-change: transform;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`

const ManufacturerLogoLink = styled.a`
  flex: none;
  margin: 0 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 320px;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover { transform: scale(1.05); }

  @media (max-width: 767px) { width: 200px; margin: 0 15px; }
  @media (min-width: 768px) and (max-width: 991px) { width: 260px; margin: 0 20px; }
`

const ManufacturerLogoImg = styled.img<{ $logoSize: number }>`
  max-height: ${p => p.$logoSize}px;
  max-width: 300px;
  width: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1) opacity(0.7);
  transition: filter 0.2s ease;

  ${ManufacturerLogoLink}:hover & {
    filter: brightness(0) invert(1) opacity(1);
  }

  @media (max-width: 767px) { max-height: 65px; max-width: 185px; }
  @media (min-width: 768px) and (max-width: 991px) { max-height: 75px; max-width: 240px; }
`

interface ManufacturersSliderProps {
  logoSize?: number
  compact?: boolean
}

const ManufacturersSlider: React.FC<ManufacturersSliderProps> = ({ logoSize, compact = false }) => {
  const effectiveLogoSize = logoSize ?? (compact ? 48 : 90)
  const containerRef = useRef<HTMLDivElement>(null)
  const { data: manufacturersData } = usePublicManufacturers()

  const manufacturerLogos = useMemo(() => {
    if (!manufacturersData) return []
    return (manufacturersData.manufacturers?.filter(m => m.image_url) || []).map(m => ({
      src: m.image_url!,
      alt: `${m.name} Logo`,
      url: m.website || '#',
    }))
  }, [manufacturersData])

  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const measure = () => setContainerWidth(el.offsetWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const [imagesReady, setImagesReady] = useState(false)

  useEffect(() => {
    if (manufacturerLogos.length === 0) return
    setImagesReady(false)
    let cancelled = false
    const srcs = [...new Set(manufacturerLogos.map(l => l.src))]
    Promise.all(srcs.map(src => new Promise<void>(resolve => {
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => resolve()
      img.src = src
      if (img.complete && img.naturalWidth > 0) resolve()
    }))).then(() => { if (!cancelled) setImagesReady(true) })
    return () => { cancelled = true }
  }, [manufacturerLogos])

  const [paused, setPaused] = useState(false)

  const totalWidth = LOGO_SLOT_PX * manufacturerLogos.length
  const numCopies = containerWidth > 0 && totalWidth > 0 ? Math.ceil(containerWidth / totalWidth) + 2 : 6
  const duration = totalWidth > 0 ? totalWidth / SCROLL_SPEED_PX_S : 10
  const allLogos = useMemo(() => Array.from({ length: numCopies }, () => manufacturerLogos).flat(), [numCopies, manufacturerLogos])

  if (!manufacturersData || manufacturerLogos.length === 0) return null

  return (
    <SliderContainer
      $loading={!imagesReady}
      $compact={compact}
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <SliderTrack
        $duration={duration}
        $paused={paused || !imagesReady}
        style={{ '--marquee-offset': `-${totalWidth}px` } as React.CSSProperties}
      >
        {allLogos.map((logo, index) => (
          <ManufacturerLogoLink
            key={`${logo.alt}-${index}`}
            href={logo.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${logo.alt.replace(' Logo', '')} website`}
            tabIndex={index < manufacturerLogos.length ? 0 : -1}
            aria-hidden={index >= manufacturerLogos.length ? true : undefined}
          >
            <ManufacturerLogoImg src={logo.src} alt={logo.alt} $logoSize={effectiveLogoSize} loading="eager" fetchPriority="high" />
          </ManufacturerLogoLink>
        ))}
      </SliderTrack>
    </SliderContainer>
  )
}

export default ManufacturersSlider
